import type { CopyVehicleScheduleFilter, CopyVehicleScheduleRequestBody } from '@/api/main/types.gen'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { getTimezone } from './datetime'

dayjs.extend(utc)
dayjs.extend(timezone)

export type VehicleScheduleMapping = {
  sourceVehicleId: string
  destinationVehicleId: string
}

const pad = (value: number): string => String(value).padStart(2, '0')

export const calendarDateInTimezone = (isoOrDate: string, timeZone: string = getTimezone()): string =>
  dayjs(isoOrDate).tz(timeZone).format('YYYY-MM-DD')

export const calendarDateToPickerDate = (date: string): Date => {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1)
}

export const pickerDateToCalendarDate = (value: Date | string): string => {
  if (typeof value === 'string') {
    return value.slice(0, 10)
  }
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
}

export const shiftCalendarDate = (date: string, days: number, timeZone: string = getTimezone()): string =>
  dayjs.tz(date, timeZone).add(days, 'day').format('YYYY-MM-DD')

export const isIdentityCopyOnSameDay = (mapping: VehicleScheduleMapping, sourceDate: string, destinationDate: string) =>
  mapping.sourceVehicleId === mapping.destinationVehicleId && sourceDate === destinationDate

export const completeMappings = (mappings: VehicleScheduleMapping[]): VehicleScheduleMapping[] =>
  mappings.filter(mapping => mapping.sourceVehicleId !== '' && mapping.destinationVehicleId !== '')

export const buildCopyVehicleScheduleBodies = (
  mappings: VehicleScheduleMapping[],
  sourceDate: string,
  destinationDate: string,
  copyScheduleFilter: CopyVehicleScheduleFilter
): CopyVehicleScheduleRequestBody[] =>
  completeMappings(mappings).map(mapping => ({
    source_vehicle_id: mapping.sourceVehicleId,
    destination_vehicle_id: mapping.destinationVehicleId,
    source_date: sourceDate,
    destination_date: destinationDate,
    copy_schedule_filter: copyScheduleFilter,
  }))

export type CopyVehicleScheduleOutcome = {
  succeeded: number
  failed: number
  failedBodies: CopyVehicleScheduleRequestBody[]
}

export const copyMappingLabel = (body: CopyVehicleScheduleRequestBody): string =>
  `${body.source_vehicle_id} → ${body.destination_vehicle_id}`

export const runCopyVehicleScheduleRequests = async (
  bodies: CopyVehicleScheduleRequestBody[],
  copyOne: (body: CopyVehicleScheduleRequestBody) => Promise<unknown>
): Promise<CopyVehicleScheduleOutcome> => {
  const results = await Promise.allSettled(bodies.map(body => copyOne(body)))
  const failedBodies = results.flatMap((result, index) => {
    const body = bodies[index]
    return result.status === 'rejected' && body ? [body] : []
  })
  return {
    succeeded: results.length - failedBodies.length,
    failed: failedBodies.length,
    failedBodies,
  }
}
